import { formatDate, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Pipe, NgModule } from '@angular/core';
import { NgbDate, NgbDateNativeUTCAdapter } from '@ng-bootstrap/ng-bootstrap';
import * as i1 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

const missingFormFieldControlErrorMessage = `
  gpu-form-field must be used with a child directive/component that implements GpuFormFieldControl (Available: gpuInput, gpu-select).
  Example:
    <gpu-form-field>
      <input type="text" gpuInput />
    </gpu-form-field>
`;
class Errors {
    static missingFormFieldControlException() {
        throw new Error(missingFormFieldControlErrorMessage);
    }
}

var KeyCodes;
(function (KeyCodes) {
    KeyCodes["TAB"] = "Tab";
    KeyCodes["ENTER"] = "Enter";
    KeyCodes["ESCAPE"] = "Escape";
    KeyCodes["SPACE"] = "Space";
    KeyCodes["ARROW_UP"] = "ArrowUp";
    KeyCodes["ARROW_DOWN"] = "ArrowDown";
})(KeyCodes || (KeyCodes = {}));

/* eslint-disable no-prototype-builtins */
class GpuLocalDatePipe {
    constructor(translate) {
        this.translate = translate;
    }
    transform(date, format = 'mediumDate') {
        if (!date) {
            return date;
        }
        if (date instanceof Date) {
            return formatDate(date, format, this.translate.currentLang ?? 'en');
        }
        else if (date instanceof NgbDate ||
            (date.hasOwnProperty('year') && date.hasOwnProperty('month') && date.hasOwnProperty('day'))) {
            return formatDate(new NgbDateNativeUTCAdapter().toModel(date), format, this.translate.currentLang ?? 'en');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLocalDatePipe, deps: [{ token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: GpuLocalDatePipe, name: "gpuLocalDate" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLocalDatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'gpuLocalDate',
                }]
        }], ctorParameters: () => [{ type: i1.TranslateService }] });

var SlideInOutState;
(function (SlideInOutState) {
    SlideInOutState["IN"] = "in";
    SlideInOutState["OUT"] = "out";
})(SlideInOutState || (SlideInOutState = {}));
const slideInOutAnimation = trigger('slideInOut', [
    state(SlideInOutState.OUT, style({ opacity: 1, height: '*', overflow: 'hidden' })),
    state(SlideInOutState.IN, style({ opacity: 0, height: 0, overflow: 'hidden' })),
    transition(`${SlideInOutState.IN} => ${SlideInOutState.OUT}`, animate('400ms ease-in-out')),
    transition(`${SlideInOutState.OUT} => ${SlideInOutState.IN}`, animate('400ms ease-in-out')),
]);

/* eslint-disable @typescript-eslint/no-explicit-any */
function coerceBooleanProperty(value) {
    return value != null && `${value}` !== 'false';
}
function coerceArray(value) {
    return Array.isArray(value) ? value : [value];
}

const round = (num, decimal = 0) => Math.round(num * Math.pow(10, decimal)) / Math.pow(10, decimal);
const range = (from, to, step = 1, decimal = 0) => Array(Math.ceil((to - from) / step))
    .fill(from)
    .map((x, y) => round(x + y * step, decimal));
const inRange = (value, from, to, first) => ((first && value >= from) || (!first && value > from)) && value <= to;

function isErrorState(control, form) {
    return !!(control && control.invalid && (control.touched || form?.submitted));
}

class GpuSharedModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuSharedModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: GpuSharedModule, declarations: [GpuLocalDatePipe], imports: [CommonModule, TranslateModule], exports: [GpuLocalDatePipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuSharedModule, providers: [GpuLocalDatePipe], imports: [CommonModule, TranslateModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuSharedModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [GpuLocalDatePipe],
                    imports: [CommonModule, TranslateModule],
                    exports: [GpuLocalDatePipe],
                    providers: [GpuLocalDatePipe],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { Errors, GpuLocalDatePipe, GpuSharedModule, KeyCodes, SlideInOutState, coerceArray, coerceBooleanProperty, inRange, isErrorState, range, round, slideInOutAnimation };
